import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { KeyRound, AlertCircle, CheckCircle2, ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { useAuthStore } from '../../store/useAuthStore.ts';
import { Button } from '../../components/Shared/Button.tsx';
import { FormField } from '../../components/Shared/FormField.tsx';
import { PasswordCriteria } from '../../components/Shared/PasswordCriteria.tsx';
import { isPasswordValid } from '../../utils/password.ts';
import { useTranslation } from '../../i18n';
import { AuthLayout } from './AuthLayout.tsx';

export function ResetPassword() {
  const { t } = useTranslation(['auth', 'common']);
  const { resetPassword } = useAuthStore();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const personalNumber = searchParams.get('pn') ?? '';

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const linkValid = token !== '' && personalNumber !== '';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!isPasswordValid(newPassword)) {
      setError(t('validation:password.invalid'));
      return;
    }
    if (newPassword !== confirmPassword) {
      setError(t('auth:resetPassword.errorMismatch'));
      return;
    }

    setIsLoading(true);
    try {
      await resetPassword(personalNumber, token, newPassword);
      setSubmitted(true);
      window.setTimeout(() => navigate('/login', { replace: true }), 2500);
    } catch {
      setError(t('auth:resetPassword.errorInvalidToken'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthLayout>
      <div className="rounded-xl border border-slate-200 bg-white">
        <div className="px-5 py-4 border-b border-slate-100">
          <div className="font-mono-num text-[10px] uppercase tracking-[0.18em] text-slate-400">
            {t('auth:resetPassword.kicker')}
          </div>
          <h2 className="text-[18px] font-semibold tracking-tight text-slate-900 mt-0.5">
            {t('auth:resetPassword.title')}
          </h2>
        </div>

        {!linkValid ? (
          <div className="p-5 space-y-4">
            <div
              role="alert"
              className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2.5"
            >
              <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" strokeWidth={2} />
              <p className="text-[12px] text-red-700">{t('auth:resetPassword.invalidLink')}</p>
            </div>
            <Link
              to="/forgot-password"
              className="inline-flex items-center gap-1.5 text-[12px] font-medium text-[#00A871] hover:underline"
            >
              <ArrowLeft className="w-3.5 h-3.5" strokeWidth={2} />
              {t('auth:resetPassword.requestNew')}
            </Link>
          </div>
        ) : submitted ? (
          <div className="p-5 space-y-4">
            <div
              role="status"
              className="flex items-start gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2.5"
            >
              <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" strokeWidth={2} />
              <p className="text-[12px] text-emerald-800">{t('auth:resetPassword.success')}</p>
            </div>
            <Link
              to="/login"
              className="inline-flex items-center gap-1.5 text-[12px] font-medium text-[#00A871] hover:underline"
            >
              <ArrowLeft className="w-3.5 h-3.5" strokeWidth={2} />
              {t('auth:resetPassword.backToLogin')}
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-5 space-y-4">
            <p className="text-[12px] text-slate-500">{t('auth:resetPassword.description')}</p>

            {error && (
              <div
                role="alert"
                className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2.5"
              >
                <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" strokeWidth={2} />
                <p className="text-[12px] text-red-700">{error}</p>
              </div>
            )}

            <FormField label={t('auth:resetPassword.newPassword')} required>
              <div className="relative">
                <input
                  id="newPassword"
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder={t('auth:login.placeholders.password')}
                  required
                  autoComplete="new-password"
                  autoFocus
                  className="w-full px-3 py-1.5 pr-10 rounded-md bg-white border border-slate-200 text-[13px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#00A871]/30 focus:border-[#00A871]/50"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={
                    showPassword
                      ? t('auth:login.hidePassword', { defaultValue: 'Passwort verbergen' })
                      : t('auth:login.showPassword', { defaultValue: 'Passwort anzeigen' })
                  }
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 focus:outline-none"
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <EyeOff className="w-4 h-4" strokeWidth={2} />
                  ) : (
                    <Eye className="w-4 h-4" strokeWidth={2} />
                  )}
                </button>
              </div>
              <PasswordCriteria password={newPassword} />
            </FormField>

            <FormField
              label={t('auth:resetPassword.confirmPassword')}
              id="confirmPassword"
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder={t('auth:login.placeholders.password')}
              required
              autoComplete="new-password"
            />

            <Button
              type="submit"
              variant="primary"
              size="lg"
              className="w-full justify-center mt-2"
              disabled={isLoading}
              leftIcon={
                isLoading ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <KeyRound className="w-4 h-4" strokeWidth={2} />
                )
              }
            >
              {isLoading ? t('auth:resetPassword.submitting') : t('auth:resetPassword.submit')}
            </Button>

            <div className="text-center">
              <Link
                to="/login"
                className="inline-flex items-center gap-1.5 text-[12px] font-medium text-slate-500 hover:text-slate-700"
              >
                <ArrowLeft className="w-3.5 h-3.5" strokeWidth={2} />
                {t('auth:resetPassword.backToLogin')}
              </Link>
            </div>
          </form>
        )}
      </div>
    </AuthLayout>
  );
}
